import React, { useState, useEffect, useCallback } from "react";
import { IEventSummary } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";
import moment from "moment";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";
import Checkbox from "src/components/Checkbox/Checkbox";

import type { ColDef } from "ag-grid-community";
import { themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";

import { RefundRequestActionsRenderer } from "./RefundRequestActionsRenderer";

import styles from "./RefundsTab.module.scss";

interface IProps {
  event: IEventSummary;
  currentTab: number;
  tabIndex: number;
}

interface IRow {
  requestedDate: string;
  ownerName: string;
  ownerEmail: string;
  dogName: string;
  showName: string;
  showDate: string;
  element: string;
  entryFee: string;
  platformFee: string;
  totalRefund: string;
  status: string;
  actions: any;
}

const RefundsTab: React.FC<IProps> = (props) => {
  const [rowData, setRowData] = useState<IRow[]>([]);
  const [allowWithoutApproval, setAllowWithoutApproval] = useState<boolean>(
    !!(props.event as any).allow_refunds_without_approval
  );
  const [savingToggle, setSavingToggle] = useState(false);

  // The club's Stripe account currency (e.g. "usd", "cad") - defaults to USD
  // for older accounts that predate this field.
  const currency = (props.event.currency || "usd").toUpperCase();

  // currencyDisplay: "code" always shows the ISO code (e.g. "USD 26.25", "CAD 26.25")
  // instead of a symbol, since a bare "$" is ambiguous — Intl renders USD as a plain
  // "$" for the en-US locale but prefixes non-native currencies (e.g. "CA$"), which
  // made it look like the currency wasn't being shown at all for USD events.
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    currencyDisplay: "code",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const loadRefundRequests = useCallback(() => {
    apiService.getEventRefundRequests(props.event._id).then((requests) => {
      const rows: IRow[] = requests.map((r: any) => ({
        requestedDate: moment(r.requested_at).format("MM/DD/YY hh:mm A"),
        ownerName: r.owner_name ?? "—",
        ownerEmail: r.owner_email ?? "—",
        dogName: r.dog_name ?? "—",
        showName: r.show_name ?? "—",
        showDate: r.show_date ? moment(r.show_date).format("MM/DD/YY") : "—",
        element: [r.show_element, r.level].filter(Boolean).join(" — "),
        entryFee: formatter.format((r.entry_fee_cents ?? 0) / 100),
        platformFee: formatter.format((r.platform_fee_cents ?? 0) / 100),
        totalRefund: formatter.format((r.total_refund_cents ?? 0) / 100),
        status: r.status,
        actions: {
          request: r,
          eventId: props.event._id,
          currency,
          onRefresh: loadRefundRequests,
          hasEntryForms:
            props.event.sanctioning_club !== Constants.sanctioning_club.GENERAL ||
            !!props.event.show_types?.some((st) => st.show_type === Constants.show_type.dock_diving),
        },
      }));
      setRowData(rows);
    });
  }, [props.event._id, currency]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (props.currentTab === props.tabIndex) {
      loadRefundRequests();
    }
  }, [props.currentTab, props.tabIndex, loadRefundRequests]);

  const handleToggleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.checked;
    setSavingToggle(true);
    try {
      await apiService.updateAllowRefundsWithoutApproval(props.event._id, newValue);
      setAllowWithoutApproval(newValue);
      toast.show({
        title: "Setting Saved",
        content: newValue
          ? "Refunds will now be processed immediately without approval."
          : "Refunds will now require approval before processing.",
        duration: 5000,
        type: "success",
      });
    } catch {
      toast.show({
        title: "Save Failed",
        content: "Could not update the setting. Please try again.",
        duration: 5000,
        type: "fail",
      });
    } finally {
      setSavingToggle(false);
    }
  };

  const theme = themeQuartz.withParams({
    fontFamily: "Roboto",
    headerFontFamily: "Roboto",
    cellFontFamily: "Roboto",
  });

  const colDefs: ColDef<IRow>[] = [
    {
      field: "status",
      headerName: "Status",
      width: 120,
      minWidth: 100,
      filter: true,
      cellRenderer: (params: any) => {
        const status: string = params.value ?? "";
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
            <span className={`${styles.statusBadge} ${styles[status]}`}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          </div>
        );
      },
    },
    { field: "dogName", headerName: "Dog", width: 160, filter: "agTextColumnFilter" },
    { field: "showName", headerName: "Show", width: 130, filter: "agTextColumnFilter" },
    { field: "showDate", headerName: "Show Date", width: 150, minWidth: 100, filter: true },
    { field: "element", headerName: "Class", minWidth: 140, filter: "agTextColumnFilter" },
    { field: "totalRefund", headerName: "Total Refund", width: 155, minWidth: 155, filter: true },
    { field: "entryFee", headerName: "Entry Fee", width: 140, minWidth: 140, filter: true },
    { field: "platformFee", headerName: "Platform Fee", width: 160, minWidth: 160, filter: true },
    { field: "ownerName", headerName: "Owner Name", minWidth: 140, filter: "agTextColumnFilter" },
    { field: "ownerEmail", headerName: "Owner Email", minWidth: 180, filter: "agTextColumnFilter" },
    { field: "requestedDate", headerName: "Requested", width: 165, minWidth: 150, filter: true },
    {
      headerName: "",
      field: "actions",
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      cellRenderer: RefundRequestActionsRenderer,
      pinned: "right",
      resizable: false,
      cellStyle: {
        overflow: "visible",
        zIndex: "2",
      },
    },
  ];

  const defaultColDef: ColDef = {};

  return (
    <div className={styles.RefundsTab}>
      <div className={styles.settings}>
        <Checkbox
          id="allow-refunds-without-approval"
          label="Allow refunds without approval"
          value={allowWithoutApproval}
          onChange={handleToggleChange}
          disabled={savingToggle}
        />
      </div>
      <div className={styles.note}>
        <strong>Note:</strong> when a refund is approved, the Entry Fee is returned to the
        exhibitor from your club&apos;s Stripe balance and the Platform Fee is refunded by Eager Dog.
        Your club is never charged the Platform Fee portion of a refund. Stripe&apos;s processing fee
        on the original payment is not returned.
      </div>
      <div className={styles.tableWrap}>
        <AgGridReact
          rowData={rowData}
          suppressRowTransform={true}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          theme={theme}
          pagination={true}
          paginationPageSize={20}
        />
      </div>
    </div>
  );
};

export default RefundsTab;
